import {Component} from "@angular/core";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {DashboardService} from "./dashboard.service";
import {ProjectService} from "../project/project.service";
import {ProjectDetailModel, projectDetailModelFactory} from "../project/project-detail.model";
import {ListEditorService} from "../list-editor/list-editor.service";
import {ListStructure} from "../list-editor/list-structure";
import {dashboardIssuesData, milestoneData} from "./dashboard-data";
import {handleErrors} from "../misc/handle-errors.decorator";
import {DashboardDataModel, dashboardDataModelFactory} from "./dashboard.model";

@Component({
  templateUrl: "./dashboard.template.pug",
  providers: [
    DashboardService,
    ProjectService,
  ],
})
export class DashboardComponent extends RouteHelper {
  dashboardData: DashboardDataModel = dashboardDataModelFactory();

  project: ProjectDetailModel = projectDetailModelFactory();
  projectId: string;
  region: string;
  deployedPercentageBar: any = {};
  title: string;
  projects: any[] = [];
  error: Error;
  disableBudgetDisplay = true;

  smartsheetUrl: SafeResourceUrl;

  paramsMap: RouteMapping[] = [
    {fieldName: "projectId", paramName: "projectId"} as RouteMapping,
    {fieldName: "region", paramName: "region"} as RouteMapping,
  ];

  dashboardIssueData = new ListStructure();
  milestoneData = new ListStructure();

  private updateAllLists() {
    const project = this.project;
    const projectService = this.projectService;
    const issues = this.dashboardIssueData;
    const milestones = this.milestoneData;

    const onChange = () => {
      project.dashboardIssues = issues.export();
      project.dashboardMilestones = milestones.export();
      return projectService.saveProject(project)
        .then(() => {
          issues.dirty = false;
          milestones.dirty = false;
        });
    };

    this.dashboardIssueData.initialize(
      dashboardIssuesData.generalData,
      dashboardIssuesData.metadata,
      this.listEditorService, // Must be injected
      this.project.dashboardIssues,
      {onChange},
    );

    this.milestoneData.initialize(
      milestoneData.generalData,
      milestoneData.metadata,
      this.listEditorService, // Must be injected
      this.project.dashboardMilestones,
      {onChange},
    );
  }

  private cleanAllLists() {
    this.dashboardIssueData.dirty = false;
    this.milestoneData.dirty = false;
  }

  private exportAllLists() {
    if (this.dashboardIssueData.dirty) this.project.dashboardIssues = this.dashboardIssueData.export();
    if (this.milestoneData.dirty) this.project.dashboardMilestones = this.milestoneData.export();
  }

  @handleErrors()
  async loadData(): Promise<any> {
    this.projects = [];
    this.smartsheetUrl = null;
    this.project = projectDetailModelFactory();
    this.dashboardData = dashboardDataModelFactory();
    this.title = undefined;
    this.deployedPercentageBar = {};

    this.dashboardData = await this.dashboardService.getDashboardData(this.projectId, this.region);
    this.disableBudgetDisplay = true;

    if (this.projectId) {
      this.disableBudgetDisplay = false;
      this.project = this.dashboardData.project;
      this.title = this.dashboardData.project.name;
      this.updateAllLists();
    } else if (!this.region) {
      this.projects = await this.dashboardService.getManagerProjects();
    }
  }

  navigate(queryParams: Params, skipLocationChange: boolean): Promise<boolean> {
    return this.router.navigate(["dashboard"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async saveProject(): Promise<void> {
    this.exportAllLists();
    await this.projectService.saveProject(this.project);
    await this.loadData();
    this.cleanAllLists();
  }

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public dashboardService: DashboardService,
    public projectService: ProjectService,
    private domSanitizer: DomSanitizer,
    private listEditorService: ListEditorService,
  ) {
    super();
  }
}
